// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
type AnyFunc = (...args: any[]) => any;

interface PatchHandles {
  unpatchElement: () => void;
  unpatchWindow: () => void;
}

const ORIG = {
  elAdd: Element.prototype.addEventListener,
  elRemove: Element.prototype.removeEventListener,
  winAdd: window.addEventListener,
  winRemove: window.removeEventListener,
};

// NOTE:
// Do NOT throttle 'wheel' or 'mousewheel'.
// These events are stateful (delta-based) and required for incremental zoom in WASM.
// Dropping or delaying events (e.g., via RAF) breaks zoom behavior and may disable preventDefault.
const HIGH_FREQ_ON_CANVAS = new Set(['mousemove', 'touchmove', 'pointermove']);
const HIGH_FREQ_ON_WINDOW = new Set(['resize']);

let listenerMap = new WeakMap<EventListenerOrEventListenerObject, EventListener>();

function toRafWrapper(listener: EventListenerOrEventListenerObject): EventListener {
  if (typeof listener === 'object' && 'handleEvent' in listener) {
    const obj = listener as EventListenerObject;
    const fn = obj.handleEvent!.bind(obj) as AnyFunc;
    return rafGate(fn);
  }
  return rafGate(listener as AnyFunc);
}

function rafGate(fn: AnyFunc): EventListener {
  let scheduled = false;
  let lastEvt: any = null;
  return (evt: Event) => {
    lastEvt = evt;
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      try { fn(lastEvt); } catch { /* swallow */ }
    });
  };
}

function patchElementAddRemove(): () => void {
  Element.prototype.addEventListener = function (this: Element, type: string, listener: any, options?: any) {
    try {
      if (this instanceof HTMLCanvasElement && (this.id === 'main_canvas') && HIGH_FREQ_ON_CANVAS.has(type)) {
        if (!listenerMap.has(listener)) {
          const wrapper = toRafWrapper(listener);
          listenerMap.set(listener, wrapper);
          return ORIG.elAdd.call(this, type, wrapper, options);
        }
        const wrapper = listenerMap.get(listener)!;
        return ORIG.elAdd.call(this, type, wrapper, options);
      }
    } catch { /* fallthrough */ }
    return ORIG.elAdd.call(this, type, listener, options);
  }

  Element.prototype.removeEventListener = function (this: Element, type: string, listener: any, options?: any) {
    try {
      if (this instanceof HTMLCanvasElement && (this.id === 'main_canvas') && HIGH_FREQ_ON_CANVAS.has(type)) {
        if (listenerMap.has(listener)) {
          const wrapper = listenerMap.get(listener);
          listenerMap.delete(listener);
          return ORIG.elRemove.call(this, type, wrapper ?? listener, options);
        }
      }
    } catch { /* fallthrough */ }
    return ORIG.elRemove.call(this, type, listener, options);
  }

  return () => {
    Element.prototype.addEventListener = ORIG.elAdd;
    Element.prototype.removeEventListener = ORIG.elRemove;
    listenerMap = new WeakMap<EventListenerOrEventListenerObject, EventListener>();
  };
}

function patchWindowAddRemove(): () => void {
  window.addEventListener = function (type: string, listener: any, options?: any) {
    if (HIGH_FREQ_ON_WINDOW.has(type)) {
      if (!listenerMap.has(listener)) {
        const wrapper = toRafWrapper(listener);
        listenerMap.set(listener, wrapper);
        return ORIG.winAdd.call(window, type, wrapper, options);
      }
      const wrapper = listenerMap.get(listener)!;
      return ORIG.winAdd.call(window, type, wrapper, options);
    }
    return ORIG.winAdd.call(window, type, listener, options);
  }

  window.removeEventListener = function (type: string, listener: any, options?: any) {
    if (HIGH_FREQ_ON_WINDOW.has(type)) {
      if (listenerMap.has(listener)) {
        const wrapper = listenerMap.get(listener);
        listenerMap.delete(listener);
        return ORIG.winRemove.call(window, type, wrapper ?? listener, options);
      }
    }
    return ORIG.winRemove.call(window, type, listener, options);
  }

  return () => {
    window.addEventListener = ORIG.winAdd;
    window.removeEventListener = ORIG.winRemove;
  }
}

let activePatch: PatchHandles | null = null;

export function installEventThrottling(): void {
  if (activePatch) return;
  const unpatchElement = patchElementAddRemove();
  const unpatchWindow = patchWindowAddRemove();
  activePatch = { unpatchElement, unpatchWindow };
}

export function uninstallEventThrottling(): void {
  if (!activePatch) return;
  try { activePatch.unpatchElement(); } catch {}
  try { activePatch.unpatchWindow(); } catch {}
  activePatch = null;
}
